import React, { useCallback, useEffect, useRef, useState, FC } from 'react';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { TooltipProps } from './types/infoToolTip.types';
import { documentService } from '../../../utils/window.utils.js';
import { iconSize } from '../../../constants/icon.constants';
import Icon from '../icon';
import PortalComponent from './portalComponent';
import './style.scss';

const InfoToolTip: FC<TooltipProps> = ({
  children,
  showIcon,
  clickPreventDefault = false,
  width = 300,
  className = '',
  iconClassName = '',
}) => {
  const TooltipIconRef = useRef<HTMLDivElement | null>(null);
  const [tooltipVisibility, setTooltipVisibility] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const customDocument = documentService();
  let tooltipDiv;
  if (customDocument && customDocument?.createElement) {
    tooltipDiv = customDocument?.createElement('div');
  }

  useEffect(() => {
    customDocument?.body.appendChild(tooltipDiv);
    return (): void => {
      customDocument?.body.removeChild(tooltipDiv);
    };
  }, [tooltipDiv]); // eslint-disable-line

  useEffect(() => {
    function handleClickOutside(e): void {
      if (isMobile && TooltipIconRef && TooltipIconRef.current && !TooltipIconRef.current.contains(e?.target)) {
        setTooltipVisibility(false);
      }
    }

    customDocument?.addEventListener('click', handleClickOutside);
    return (): void => customDocument?.removeEventListener('click', handleClickOutside);
  }, [isMobile, TooltipIconRef, customDocument]);

  const showTooltip = (): void => {
    setTooltipVisibility(true);
  };

  const hideTooltip = (): void => {
    setTooltipVisibility(false);
  };

  const handleMouseOver = (): void => {
    if (!isMobile) {
      showTooltip();
    }
  };

  const handleMouseOut = (): void => {
    if (!isMobile) {
      hideTooltip();
    }
  };

  const handleClick = (event): void => {
    if (isMobile) {
      showTooltip();
    }
    if (clickPreventDefault) {
      event.preventDefault();
    }
  };

  const handleBlur = (): void => {
    if (isMobile) {
      hideTooltip();
    }
  };

  const getTooltipIconDimensions = useCallback((): DOMRect | null | undefined => {
    const toolTipCurrent = TooltipIconRef?.current as HTMLElement | null;
    if (toolTipCurrent) {
      return toolTipCurrent.getBoundingClientRect();
    }
    return null;
  }, [TooltipIconRef]);

  if (!showIcon) return null;

  return (
    <div className={`info-tooltip-sep ${className}`}>
      <div
        className="info-tooltip-sep__icon"
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onClick={handleClick}
        onBlur={handleBlur}
        role="tooltip"
      >
        {showIcon && (
          <div ref={TooltipIconRef}>
            <Icon iconName="info-alert-notif-info" size={iconSize.mbl} extraClassName={iconClassName} />
          </div>
        )}
      </div>
      <PortalComponent
        tooltipDiv={tooltipDiv}
        getTooltipIconDimensions={getTooltipIconDimensions}
        width={width}
        tooltipVisibility={tooltipVisibility}
      >
        {children}
      </PortalComponent>
    </div>
  );
};

export default InfoToolTip;
